import { useEffect, useMemo, useState } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { usePermissions } from 'hooks/usePermissions';
import { MultiLevelMenuItem } from 'layouts/components/Menu/constants/menuList';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useSidebarState } from 'react-admin';
import { Collapse } from 'shared/mui/Collapse/Collapse';
import { List, ListItemIcon, ListItemText } from 'shared/mui/List';
import { ListItemButton } from 'shared/mui/List/ListItemButton';
import { Tooltip } from 'shared/mui/Tooltip';
import { Permission } from 'types/embedded/permission';

import { MenuItemLink } from './MenuItemLink/MenuItemLink';
import { MenuItemLinkWithCounter } from './MenuItemLinkWithCounter/MenuItemLinkWithCounter';

type Props = {
  multiLevelMenuItem: MultiLevelMenuItem;
  setCurrentResourceMenuItem: (resource: string | null) => void;
  currentResourceMenuItem: string | null;
};

export const MenuCollapseItem: React.FC<Props> = ({
  multiLevelMenuItem: { title, icon, items },
  setCurrentResourceMenuItem,
  currentResourceMenuItem,
}) => {
  const translate = useTypedTranslate();
  const [openSidebar] = useSidebarState();
  const [openList, setOpenList] = useState(false);

  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const RESOURCES_LIST = useMemo(() => items.map(({ resource }) => resource), [items]);

  const isShowResourceSubMenu = useMemo(
    () =>
      RESOURCES_LIST?.some(
        (resource) =>
          hasResourceAccess(resource) &&
          hasResourcePermissions(resource, [Permission.Full, Permission.Read]),
      ),
    [RESOURCES_LIST, hasResourcePermissions, hasResourceAccess],
  );

  const handleOpenList = () => {
    openSidebar
      ? setOpenList((prev) => !prev)
      : setCurrentResourceMenuItem(currentResourceMenuItem === title ? null : title);
  };

  useEffect(() => {
    setOpenList(false);
    setCurrentResourceMenuItem(null);
  }, [openSidebar]);

  if (!isShowResourceSubMenu) {
    return <></>;
  }

  return (
    <>
      <Tooltip placement="right" title={!openSidebar ? translate(title) : ''}>
        <ListItemButton className="MuiMenuItem-root" onClick={handleOpenList}>
          <ListItemIcon sx={{ marginLeft: '-4px', minWidth: '40px' }}>{icon}</ListItemIcon>
          <ListItemText
            primary={openSidebar ? translate(title) : ''}
            sx={{ color: 'rgba(0,0,0,0.87)' }}
          />
          {openSidebar && (
            <>
              {openList ? (
                <ExpandLess sx={{ color: 'rgba(0,0,0,0.6)' }} />
              ) : (
                <ExpandMore sx={{ color: 'rgba(0,0,0,0.6)' }} />
              )}
            </>
          )}
        </ListItemButton>
      </Tooltip>
      <Collapse in={openList} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map(({ resource, title, counter }) => {
            const props = {
              primaryText: translate(title),
              resource,
              sx: { paddingLeft: '54px' },
              to: `/${resource}`,
            };

            if (counter) {
              return <MenuItemLinkWithCounter counter={counter} key={title} {...props} />;
            }
            return <MenuItemLink key={title} {...props} />;
          })}
        </List>
      </Collapse>
    </>
  );
};
